import {
  defaultInventoryFor,
  formatWeight,
  fromCanonicalKg,
  roundWeight,
  toCanonicalKg,
  type PlateInventoryEntry,
  type WeightUnitLabels,
  parseLocaleNumber,
} from '@heraion/shared';
import type { WeightUnit } from './state/loggerDraft';

// Pure plate-calculator math extracted out of LoggerScreen's
// onOpenPlatesForSet / onApplyPlateCalc (Track R). Kept apart from the
// state + dispatch in usePlateCalc so the seed/format/target rules are
// unit-testable. Structural param types so callers pass DraftSet[]
// without coupling this file to the draft module.

type WeightedSet = { weight: string; unit: WeightUnit };
type CompletableSet = { completedAt: string | null };

/**
 * The locale the weight formatters below must speak, read off `useTranslation()`'s
 * `i18n`. ⛔ It is a FUNCTION and not a `?.` at each callsite: an Italian read
 * "1.25 kg" in this modal and "1,25 kg" in the history row of the same screen,
 * because `formatWeight`'s `locale` default (`"en-US"`) was the only thing
 * feeding it here while the other ~20 callsites in the app passed the locale.
 * One fallback, written once, is what stops that from happening again in the
 * next section that formats a weight.
 *
 * ⚠️ `i18n` is OPTIONAL on purpose, and that is not defensive noise: the app's
 * tests mock `react-i18next` with a `t` and nothing else
 * (`plateCompose.test.tsx:25`), so a bare `i18n.resolvedLanguage` throws inside
 * the component under every one of them. `resolvedLanguage` first because it is
 * the language actually in force after fallback; `language` is what a mock or a
 * half-initialised i18next has instead.
 */
export const WEIGHT_LOCALE_FALLBACK = 'en-US';

export function weightLocale(i18n?: {
  resolvedLanguage?: string;
  language?: string;
}): string {
  return i18n?.resolvedLanguage ?? i18n?.language ?? WEIGHT_LOCALE_FALLBACK;
}

/**
 * Render a working-unit number BARE — no unit suffix. For the weight field and
 * the plate chips, where the unit is already said once beside them and saying
 * it again on every chip would turn one row of weights into a row of sentences.
 *
 * ⚠️ The argument is in `unit`, NOT canonical kilos: this is the modal's own
 * number, the one the athlete is looking at. `roundWeight(…, 'display')` is
 * what decides the precision — grams in kg, tenths in lb — so a 2.5 lb plate
 * reads "2.5" and never "2.4999999".
 *
 * ⛔ Replaces the old `formatKg`, which was `toFixed(2)` on a value this modal
 * simply assumed was kilos. That assumption IS the bug of LG-37b E1: the
 * athlete switched the row to lb and every number here stayed metric.
 *
 * ⛔ `useGrouping: false`, and that is the whole reason this function was
 * locale-BLIND until now. It is the string that goes INTO the target
 * `TextInput` and comes back out through `parseLocaleNumber`, which swaps the
 * first comma for a dot and calls `parseFloat`: what breaks that round trip is
 * the THOUSANDS SEPARATOR — an it-IT `Intl` writes 1250 kg as "1.250", read
 * back as 1.25 — and not the decimal mark, which round-trips in all five
 * languages we ship. So the grouping goes, the locale stays, and an Italian
 * reads "2,5" on the chip beside the "2,5 kg" of the summary instead of the
 * "2.5" that used to sit two centimetres away from it.
 *
 * ⛔ Which also means the callers no longer patch the separator by hand:
 * `PlateCalcCalculatorSection` used to do `.split('.').join(decimal)` on the
 * way out of here. Curing it at the callsite left every other caller wrong.
 *
 * ⚠️ `locale` is REQUIRED for the same reason it is on `formatUnitWeight`: a
 * default would be `"en-US"` and the next caller would forget it quietly.
 * Callers get it from `weightLocale(i18n)` above.
 */
export function formatUnitValue(
  value: number,
  unit: WeightUnit,
  locale: string,
): string {
  if (!Number.isFinite(value)) return '0';
  // The same precision as before — `roundWeight(…, 'display')` is what decides
  // it (grams in kg, tenths in lb); `Intl` is only allowed to WRITE it.
  return new Intl.NumberFormat(locale, {
    minimumFractionDigits: 0,
    maximumFractionDigits: unit === 'kg' ? 3 : 1,
    useGrouping: false,
  }).format(roundWeight(value, unit, 'display'));
}

/**
 * The same number WITH its unit — "20 kg", "45 lb". For the places a weight is
 * read on its own: the bar chips, and the nine translated strings that now
 * interpolate `{{weight}}` instead of a bare `{{kg}}` under a hard-coded suffix.
 *
 * ⛔ The suffix is not concatenated here: `formatWeight` is the app-wide weight
 * formatter and it already carries it. A second `${n} ${unit}` in this file
 * would be a second answer to "how does a weight look", and the German string
 * that puts the verb last needs the WHOLE weight interpolated as one token.
 *
 * ⚠️ `formatWeight` speaks canonical kilos, so a working-unit value takes the
 * round trip through `toCanonicalKg`. That trip costs nothing readable: it
 * rounds to grams and comes back through the same `display` rule as above.
 *
 * ⛔ `locale` is REQUIRED, not defaulted: `formatWeight` already has a default
 * and it is `"en-US"`, which is exactly how this modal came to say "1.25 kg"
 * under a history row saying "1,25 kg". A required argument means the next
 * section that formats a weight cannot forget it quietly — it fails to compile.
 * Callers get it from `weightLocale(i18n)` above.
 */
export function formatUnitWeight(
  value: number,
  unit: WeightUnit,
  locale: string,
  labels: WeightUnitLabels,
): string {
  if (!Number.isFinite(value)) return formatWeight(0, unit, locale, labels);
  return formatWeight(toCanonicalKg(value, unit), unit, locale, labels);
}

// ⭐ `unitWord` USED to live here, and CN-26 wave 2 moved it to
// `../weightUnitLabels`. Its old docblock promised exactly this move — *"taking
// the unit out of formatWeight is CN-26 ondata 2, not this slice"* — and that
// is now done: `formatWeight` takes the translated words as a bundle, so the
// «фунт» over «lb» disagreement is gone from every screen, not just this one.
// It was never a plate concept; it lived here because this was the first screen
// that needed the unit spelled right.

/**
 * Seed the calculator from the set the user is editing: take the focused
 * set's weight (canonical kg) when it has a usable value, else walk
 * backwards to the last non-zero set in the entry. Returns 0 when
 * nothing usable exists (modal opens at zero).
 */
export function seedKgFromSets(
  sets: ReadonlyArray<WeightedSet>,
  focusedIdx: number,
): number {
  const focused = sets[focusedIdx];
  if (focused) {
    const v = parseLocaleNumber(focused.weight);
    if (Number.isFinite(v) && v > 0) return toCanonicalKg(v, focused.unit);
  }
  for (let i = sets.length - 1; i >= 0; i--) {
    const candidate = sets[i];
    if (!candidate) continue;
    const v = parseLocaleNumber(candidate.weight);
    if (Number.isFinite(v) && v > 0) return toCanonicalKg(v, candidate.unit);
  }
  return 0;
}

/**
 * Convert the chosen total kg back to the set's display unit and format
 * to 2 dp as the string the reducer stores.
 */
export function formatPlateWeight(absKg: number, unit: WeightUnit): string {
  const value = fromCanonicalKg(absKg, unit);
  return (Math.round(value * 100) / 100).toString();
}

/**
 * Resolve which set apply writes to: the modal's setIdx when present,
 * else the first pending (uncompleted) set, else the last set.
 */
export function resolveApplyTargetSetIdx(
  sets: ReadonlyArray<CompletableSet>,
  setIdx: number | null,
): number {
  if (setIdx !== null) return setIdx;
  const pending = sets.findIndex((s) => s.completedAt === null);
  return pending === -1 ? sets.length - 1 : pending;
}

/**
 * The plate sizes a row of controls should offer, heaviest first.
 *
 * "Active" means the athlete owns at least one pair: a size at zero drops out,
 * which is how the compose row is meant to be shortened (Antenore, 10/09:
 * «se lo metto a 0 sotto sparisce, può essere accettabile»).
 *
 * `withDefaults` is the inventory EDITOR's version, which also has to offer a ±
 * for every commercial size the athlete does not own yet — and, crucially, for
 * one they own but that is no longer a default. Without the union a stored
 * 0.5 kg plate would have a chip in the compose row and no way to reach zero.
 *
 * ⚠️ `unit` picks WHICH commercial rack those defaults are: a pound gym racks
 * 45/35/25/10/5/2.5 lb, which is what is written on that iron and not the
 * metric list run through 2.2046 (`equipment-model.md` «L'invariante che sopravvive a tutto»).
 */
export function activeDenominations(
  inventory: ReadonlyArray<PlateInventoryEntry>,
  unit: WeightUnit,
  { withDefaults = false }: { withDefaults?: boolean } = {},
): number[] {
  const owned = inventory
    .filter((e) => e.size > 0 && e.pairs > 0)
    .map((e) => e.size);
  const sizes = withDefaults
    ? [...defaultInventoryFor(unit).map((e) => e.size), ...owned]
    : owned;
  return Array.from(new Set(sizes)).sort((a, b) => b - a);
}
